import {
  type UseMutationOptions,
  mutationOptions,
} from '@tanstack/react-query';

import { BRAND_SERVICE_VERSION_HEADERS } from '@/constants/environments';
import apiClient from '@/constants/http';
import type { Service } from '@/types/booking';
import {
  omitKnownCustomerDetails,
  transformCartIntoPayloadCart,
} from '@/utils/bookingCartUtils';
import { getSessionCustomerId } from '@/utils/syncCustomerIdFromSession';

import type { UpsertCartResponse } from './types';

/**
 * Creates or updates a cart with multiple guests, each with their own services and professional
 * @param params - Cart upsert parameters including guests array
 * @returns Promise<UpsertCartResponse> - The created or updated cart data
 */
const upsertCart = async ({
  services,
  cartId,
  shopId,
  customerId,
  ...cart
}: Parameters<typeof transformCartIntoPayloadCart>[0] & {
  services: Service[];
  shopId: string;
  cartId?: string;
  customerId?: string;
}): Promise<UpsertCartResponse> => {
  if (!shopId) {
    throw new Error('Shop ID is required to create a cart');
  }

  const resolvedCustomerId = (await getSessionCustomerId()) ?? customerId;
  const payload = omitKnownCustomerDetails(
    transformCartIntoPayloadCart(cart, resolvedCustomerId, services),
    resolvedCustomerId,
  );

  // Make API request with required headers
  const method = cartId ? 'put' : 'post';
  const { data } = await apiClient[method]<UpsertCartResponse>(
    `/v2/shop/${shopId}/cart${cartId ? `/${cartId}` : ''}`,
    payload,
    { headers: BRAND_SERVICE_VERSION_HEADERS },
  );

  // Validate response data
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid response format: expected cart data object');
  }

  if (!data.id || typeof data.total !== 'number' || !data.currency) {
    throw new Error(
      'Invalid cart response: missing required fields (id, total, currency)',
    );
  }

  return data;
};

export const upserCartMutation = (
  options?: UseMutationOptions<
    UpsertCartResponse,
    Error,
    Parameters<typeof upsertCart>[0]
  >,
) =>
  mutationOptions<UpsertCartResponse, Error, Parameters<typeof upsertCart>[0]>({
    ...options,
    retry: false,
    mutationFn: upsertCart,
    retryDelay: (attemptIndex: number = 0) =>
      Math.min(1000 * 2 ** attemptIndex, 2000),
    onMutate: async (variables, ctx) => {
      if (!variables.cartId) {
        ctx.client.setQueriesData(
          { queryKey: ['cart', variables.shopId] },
          null,
        );
      }

      // Propagate the inner onMutate's return value as the mutation context so
      // onError receives it (e.g. the previousCart snapshot used to revert a
      // rejected promo/gift-card code — SQR-12917).
      return options?.onMutate?.(variables, ctx);
    },
  });
